<template>
  <div class="tg-page-root package-page bg-gradient-to-br from-violet-50 via-white to-violet-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
    <!-- Header (pinned) -->
    <div class="package-page-header flex-shrink-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
      <div class="flex items-center gap-2 px-4 h-14">
        <button
          type="button"
          aria-label="back"
          @click="goBack"
          class="package-back-btn flex items-center justify-center w-9 h-9 rounded-xl text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 active:scale-95 transition-all flex-shrink-0"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-[18px] font-semibold text-gray-900 dark:text-gray-100 flex-1 text-left truncate">{{ $t('PageTitle_Choose_Plan') }}</h1>
        <div class="w-9 flex-shrink-0"></div>
      </div>
    </div>

    <!-- Country info (pinned below header) -->
    <div class="package-country-section flex-shrink-0 px-4 pt-2 pb-2">
      <div class="package-hero-card">
        <div class="package-hero-main">
          <div class="package-hero-flag">
            <div
              :class="`flag-icon flag-icon-${getFlagCode(countryCode).toLowerCase()}`"
              class="package-hero-flag-img"
              :data-country="countryCode"
              @error="handleFlagError"
              style="background-size: contain; background-position: center; background-repeat: no-repeat;"
            ></div>
            <div v-if="flagError" class="package-hero-flag-fallback">Global</div>
          </div>
          <div class="package-hero-text">
            <h2 class="package-hero-title">{{ displayCountryName(countryCode) }}</h2>
            <p class="package-hero-meta">
              <span class="package-hero-type">{{ isSim ? $t('SIM_Card') : $t('eSIM') }}</span>
              <span class="package-hero-meta-dot">·</span>
              <span class="package-hero-subtitle">{{ $t('Choose_a_data_plan') }}</span>
            </p>
          </div>
        </div>
        <button
          v-if="coverageAreaList.length > 0"
          type="button"
          class="package-hero-coverage"
          @click="openCoverageDialog"
        >
          <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="flex-1 text-left">{{ $t('Package_Coverage_Count', coverageAreaList.length, { count: coverageAreaList.length }) }}</span>
          <svg class="w-4 h-4 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Scrollable plan list -->
    <div ref="plansScrollRef" class="package-plans-scroll">
      <!-- SIM Card: matrix-style plan selector -->
      <div v-if="isSim" class="sim-plan-selector">
        <div class="package-summary-card">
          <p class="package-summary-label">{{ $t('Package_Selected_Plan') }}</p>
          <div class="package-summary-main">
            <div class="package-summary-info min-w-0">
              <p v-if="currentPlan && selectedDataLabel && selectedDays" class="package-summary-plan">
                <span class="package-summary-plan-strong">{{ selectedDataLabel }}</span>
                <span class="package-summary-plan-dot">·</span>
                <span>{{ $t('Package_Detail_Days_Format', { count: selectedDays, days: selectedDays }) }}</span>
              </p>
              <p v-else class="package-summary-empty">{{ $t('Choose_a_data_plan') }}</p>
              <p class="package-summary-sms">
                <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                {{ $t('Package_SMS_Badge') }}
              </p>
            </div>
            <button
              v-if="currentPlan"
              type="button"
              class="package-summary-detail-link flex-shrink-0"
              @click="openPlanDetail(selectedPlan)"
            >
              {{ $t('Package_Detail_Link') }}
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Data options -->
        <section class="sim-section-card">
          <div class="sim-section-head">
            <span class="sim-section-icon sim-section-icon--data">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </span>
            <h3 class="sim-section-title">{{ $t('Data') }}</h3>
          </div>
          <div class="sim-data-grid">
            <button
              v-for="opt in dataOptions"
              :key="opt.key"
              type="button"
              :disabled="!isDataOptionAvailable(opt.key)"
              :class="[
                'sim-option-btn',
                opt.fullWidth ? 'col-span-2' : '',
                !isDataOptionAvailable(opt.key) ? 'sim-option-btn--disabled' : '',
                selectedDataKey === opt.key ? 'sim-option-btn--active' : 'sim-option-btn--default'
              ]"
              @click="selectDataOption(opt.key)"
            >
              <span class="sim-option-content" :class="{ 'sim-option-strike': !isDataOptionAvailable(opt.key) }">
                <svg
                  v-if="selectedDataKey === opt.key && isDataOptionAvailable(opt.key)"
                  class="sim-option-check-inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg>
                <span class="sim-option-label">{{ opt.label }}</span>
              </span>
            </button>
          </div>
        </section>

        <!-- Service days -->
        <section class="sim-section-card">
          <div class="sim-section-head">
            <span class="sim-section-icon sim-section-icon--days">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </span>
            <h3 class="sim-section-title">{{ $t('Package_Service_Days') }}</h3>
          </div>
          <div v-if="showDaySelector" class="sim-days-grid">
            <button
              v-for="days in availableDayOptions"
              :key="days"
              type="button"
              :class="[
                'sim-option-btn sim-day-btn',
                selectedDays === days ? 'sim-option-btn--active' : 'sim-option-btn--default'
              ]"
              @click="selectDaysOption(days)"
            >
              <span class="sim-day-label">
                <span class="sim-day-num">{{ days }}</span>
                <span class="sim-day-unit">{{ $t('Days') }}</span>
              </span>
            </button>
          </div>
          <p v-else-if="singleDayDisplay != null" class="sim-days-info">
            {{ $t('Package_Detail_Days_Format', { count: singleDayDisplay, days: singleDayDisplay }) }}
          </p>
        </section>
      </div>

      <!-- eSIM: medium-density plan cards -->
      <div v-else class="package-esim-list">
        <div
          v-for="(plan, index) in planList"
          :key="index"
          role="button"
          tabindex="0"
          class="esim-plan-card"
          :class="{ 'esim-plan-card--active': selectedPlan === index }"
          @click="selectPlan(index)"
          @keydown.enter.prevent="selectPlan(index)"
        >
          <div
            v-if="index === 0"
            class="esim-plan-badge-float"
          >
            <svg class="esim-plan-badge-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            {{ $t('Most_Popular') }}
          </div>

          <div class="esim-plan-inner">
            <div class="esim-plan-info">
              <div class="esim-plan-gb-row">
                <span class="esim-plan-gb-num">{{ plan.GB }}</span>
                <span class="esim-plan-gb-unit">GB</span>
              </div>

              <div class="esim-plan-validity-row">
                <svg class="esim-plan-validity-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  {{ $t('Package_Detail_Days_Format', { count: plan.ValidityDays, days: plan.ValidityDays }) }}
                </span>
              </div>

              <div class="esim-plan-price-row">
                <span class="esim-plan-price">${{ formatPlanPrice(plan.PriceUSD) }}</span>
                <span class="esim-plan-unit">${{ formatPlanUnitPrice(plan) }}/GB</span>
              </div>

              <button
                type="button"
                class="esim-plan-detail-link"
                @click.stop="openPlanDetail(index)"
              >
                {{ $t('Package_Detail_Link') }}
                <svg class="esim-plan-detail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <span
              class="esim-plan-radio"
              :class="{ 'esim-plan-radio--active': selectedPlan === index }"
              aria-hidden="true"
            >
              <span v-if="selectedPlan === index" class="esim-plan-radio-core">
                <svg
                  class="esim-plan-radio-check"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg>
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Checkout bar -->
    <div
      v-show="!showPaySheet && !showCouponSelector"
      class="mobile-frame-fixed checkout-bar-wrap fixed bottom-0 z-30 pb-[env(safe-area-inset-bottom)]"
    >
      <div class="checkout-bar-inner">
        <div v-if="currentPlan" class="checkout-bar-panel">
          <div class="checkout-bar-price">
            <span class="checkout-price-label">{{ $t('Package_Actual_Pay') }}</span>
            <p class="checkout-price-row">
              <span class="checkout-price-amount">${{ displayPrice }}</span>
              <span class="checkout-price-currency">USD</span>
            </p>
          </div>
          <button
            type="button"
            class="checkout-buy-btn"
            :disabled="selectedPlan === null"
            @click="openPaySheet"
          >
            <span class="checkout-action-text">{{ $t('Package_Buy_Now') }}</span>
            <svg class="checkout-action-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <button
          v-else
          type="button"
          class="checkout-bar-empty"
          disabled
        >
          {{ $t('Choose_a_data_plan') }}
        </button>
      </div>
    </div>

    <!-- Confirm Pay Bottom Sheet -->
    <transition name="fade">
      <div
        v-if="showPaySheet"
        class="fixed inset-0 bg-black/40 z-[75]"
        @click.self="closePaySheet"
      ></div>
    </transition>
    <transition name="slide-up">
      <div
        v-if="showPaySheet"
        class="mobile-frame-fixed pay-sheet-anchor fixed bottom-0 z-[80]"
      >
        <div class="pay-sheet-wrap px-3 sm:px-4">
        <div
          class="pay-sheet-panel bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl border-t border-gray-100 dark:border-gray-800 w-full max-w-[430px] mx-auto"
        >
          <div class="flex justify-center pt-2.5 pb-2">
            <div class="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></div>
          </div>

          <div ref="paySheetBodyRef" class="pay-sheet-body px-4 tg-sheet-scroll">
            <!-- Product summary (compact) -->
            <div class="pay-sheet-product">
              <div class="pay-sheet-flag-wrap">
                <div
                  :class="`flag-icon flag-icon-${getFlagCode(countryCode).toLowerCase()}`"
                  class="pay-sheet-flag"
                  :data-country="countryCode"
                  @error="handleFlagError"
                  style="background-size: contain; background-position: center; background-repeat: no-repeat;"
                ></div>
                <div v-if="flagError" class="pay-sheet-flag-fallback">Global</div>
              </div>
              <div class="pay-sheet-product-text min-w-0">
                <template v-if="currentPlan">
                  <p class="pay-sheet-product-country">{{ displayCountryName(countryCode) }}</p>
                  <p class="pay-sheet-product-detail">
                    {{ currentPlan.GB }}GB · {{ $t('Package_Detail_Days_Format', { count: currentPlan.ValidityDays, days: currentPlan.ValidityDays }) }} · {{ isSim ? $t('SIM_Card') : $t('eSIM') }}
                  </p>
                </template>
                <p v-else class="pay-sheet-product-country">{{ $t('Package_Plan_Info') }}</p>
              </div>
            </div>
            <div class="pay-sheet-divider" aria-hidden="true"></div>

            <!-- Promo: discount code + coupon -->
            <div class="pay-sheet-promo">
              <div class="pay-sheet-discount-block">
                <div class="pay-sheet-discount-head">
                  <span class="pay-sheet-label">{{ $t('Discount_Code') }}</span>
                  <div v-if="appliedDiscountCode" class="pay-sheet-row-value">
                    <span class="text-brand font-semibold text-sm">{{ appliedDiscountCode }}</span>
                    <button type="button" class="text-gray-500 dark:text-gray-300 text-xs underline ml-2" @click="clearDiscountCode">
                      {{ $t('Discount_Code_Clear') }}
                    </button>
                  </div>
                </div>
                <div
                  v-if="!appliedDiscountCode"
                  class="pay-sheet-discount-row"
                  :class="{ 'pay-sheet-discount-row--disabled': !!selectedCouponId }"
                >
                  <input
                    ref="discountInputRef"
                    v-model="discountCodeInput"
                    type="text"
                    inputmode="text"
                    autocomplete="off"
                    autocapitalize="characters"
                    class="pay-sheet-input"
                    :placeholder="$t('Discount_Code_Placeholder')"
                    :disabled="!!selectedCouponId || discountCodeApplying"
                    @focus="onPaySheetInputFocus"
                    @blur="onPaySheetInputBlur"
                    @keyup.enter="applyDiscountCode"
                  />
                  <button
                    type="button"
                    class="pay-sheet-apply-btn"
                    :disabled="!discountCodeInput.trim() || !!selectedCouponId || discountCodeApplying"
                    @click="applyDiscountCode"
                  >
                    {{ discountCodeApplying ? '…' : $t('Discount_Code_Apply') }}
                  </button>
                </div>
                <transition name="pay-sheet-collapse">
                  <p v-if="selectedCouponId && !appliedDiscountCode" class="pay-sheet-hint">
                    {{ $t('Discount_Code_Disabled_With_Coupon') }}
                  </p>
                </transition>
              </div>

              <button
                type="button"
                class="pay-sheet-row pay-sheet-row--btn"
                :class="{ 'opacity-50 pointer-events-none': appliedDiscountCode }"
                @click="openCouponSelector"
              >
                <span class="pay-sheet-label">{{ $t('Coupon') }}</span>
                <span class="pay-sheet-coupon-value">
                  <span v-if="couponLoading" class="text-gray-400 dark:text-gray-300 text-sm">…</span>
                  <span v-else-if="selectedCouponId" class="pay-sheet-coupon-selected">
                    {{ getCouponLabel(availableCoupons.find(c => c.id === selectedCouponId)) }}
                  </span>
                  <span v-else class="pay-sheet-coupon-placeholder">
                    {{ availableCoupons.length > 0 ? $t('Select_Coupon_Placeholder') : $t('No_Coupons_Available') }}
                  </span>
                  <svg class="pay-sheet-coupon-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </button>

              <transition name="pay-sheet-collapse">
                <div v-if="Number(discountAmount) > 0" class="pay-sheet-row pay-sheet-row--tight">
                  <span class="pay-sheet-label">{{ $t('Discount_Amount', 'Discount') }}</span>
                  <span class="pay-sheet-discount-value">-${{ discountAmount }}</span>
                </div>
              </transition>
            </div>

            <div v-if="showPaymentMethodPicker" class="pay-sheet-payment-methods">
              <p class="pay-sheet-label pay-sheet-payment-methods-title">{{ $t('Payment_Method_Label') }}</p>
              <div class="pay-sheet-payment-methods-grid">
                <button
                  v-for="method in availablePaymentMethods"
                  :key="method"
                  type="button"
                  class="pay-sheet-payment-option"
                  :class="{ 'pay-sheet-payment-option--active': selectedPaymentMethod === method }"
                  @click="selectedPaymentMethod = method"
                >
                  <span class="pay-sheet-payment-option-title">{{ getPaymentMethodTitle(method) }}</span>
                  <span class="pay-sheet-payment-option-desc">{{ getPaymentMethodDesc(method) }}</span>
                </button>
              </div>
            </div>

          </div>

          <!-- Fixed footer: total + agreement + pay -->
          <div class="pay-sheet-footer px-4">
            <div class="pay-sheet-total">
              <span class="pay-sheet-total-label">{{ $t('Total_Amount', 'Total Amount') }}</span>
              <div v-if="priceLoading" class="animate-pulse h-7 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div v-else class="pay-sheet-total-prices">
                <span
                  v-if="Number(discountAmount) > 0"
                  class="pay-sheet-original-price"
                >{{ formatPaySheetPrice(paySheetOriginalPrice, paySheetOriginalPriceRub) }}</span>
                <span class="pay-sheet-total-price">{{ formatPaySheetPrice(finalPrice, finalPriceRub) }}</span>
              </div>
            </div>

            <label class="pay-sheet-agree" for="agreeReturnPolicy">
              <input
                id="agreeReturnPolicy"
                type="checkbox"
                v-model="agreeReturnPolicy"
                class="pay-sheet-checkbox"
              />
              <span class="pay-sheet-agree-text">
                {{ $t('Package_Agree_Return_Prefix') }}
                <router-link
                  to="/return-policy"
                  class="pay-sheet-policy-link"
                  @click.stop
                >
                  {{ $t('Package_Return_Policy_Link_Text') }}
                </router-link>
              </span>
            </label>

            <button
              type="button"
              class="pay-sheet-confirm-btn"
              @click="confirmPay"
              :disabled="!agreeReturnPolicy || isPaying"
            >
              {{ isPaying ? $t('Payment_Processing') : $t('Confirm_Payment') }}
            </button>
          </div>
        </div>
        </div>
      </div>
    </transition>

    <!-- Coupon Selector Bottom Sheet (above pay sheet) -->
    <transition name="fade">
      <div
        v-if="showCouponSelector"
        class="coupon-selector-overlay fixed inset-0 bg-black/40 z-[85]"
        @click.self="closeCouponSelector"
      ></div>
    </transition>
    <transition name="slide-up">
      <div v-if="showCouponSelector" class="coupon-selector-sheet-wrap mobile-frame-fixed tg-bottom-sheet-anchor fixed bottom-0 z-[90]">
        <div class="coupon-selector-sheet tg-bottom-sheet-panel coupon-sheet-panel bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl border-t border-gray-100 dark:border-gray-800 max-h-[70vh] flex flex-col w-full">
          <!-- Header -->
          <div class="coupon-selector-header flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
            <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ $t('Select_Coupon_Placeholder', 'Select Coupon') }}</h3>
            <button
              @click="closeCouponSelector"
              class="coupon-selector-close w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-700 active:scale-95 transition-all"
            >
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Coupon List -->
          <div class="coupon-selector-body p-4 overflow-y-auto overflow-x-hidden tg-sheet-scroll flex-1 space-y-3 bg-gray-50 dark:bg-gray-900">
             <!-- No Coupon Option -->
             <div 
                @click="selectCoupon(null)"
                class="bg-white dark:bg-gray-900 p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center justify-between"
                :class="selectedCouponId === null ? 'border-brand ring-1 ring-brand/20' : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'"
             >
                <span class="text-gray-700 dark:text-gray-300 font-medium">{{ $t('Don_t_use_coupon', "Don't use coupon") }}</span>
                <div v-if="selectedCouponId === null" class="w-5 h-5 rounded-full bg-brand flex items-center justify-center">
                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <div v-else class="w-5 h-5 rounded-full border-2 border-gray-300"></div>
             </div>

             <!-- Available Coupons -->
             <div v-for="coupon in availableCoupons" :key="coupon.id" 
                @click="selectCoupon(coupon.id)"
                class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border-2 overflow-hidden cursor-pointer transition-all relative"
                :class="selectedCouponId === coupon.id ? 'border-brand ring-1 ring-brand/20' : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'"
             >
                <div class="flex">
                    <!-- Left: Value -->
                    <div class="w-24 bg-brand/5 flex flex-col items-center justify-center p-2 border-r border-dashed border-brand/20">
                        <div class="text-brand font-bold text-lg text-center leading-tight">
                            <span v-if="coupon.coupon_type?.type === 1">
                                {{ formatDiscount(coupon.coupon_type.discount_rate) }}
                            </span>
                            <span v-else-if="coupon.coupon_type?.type === 2">
                                ${{ coupon.coupon_type.cash_value }}
                            </span>
                            <span v-else-if="coupon.coupon_type?.type === 3">
                                -${{ coupon.coupon_type.reduction_amount }}
                            </span>
                            <span v-else-if="coupon.coupon_type?.type === 4">
                                {{ formatDiscount(coupon.coupon_type.extension?.discount_rate) }}
                            </span>
                            <span v-else>{{ $t(coupon.coupon_type?.name) }}</span>
                        </div>
                         <div class="text-[10px] text-brand/80 mt-1 text-center px-1" v-if="coupon.coupon_type?.type === 4">
                             {{ $t('Buy_Items', { count: coupon.coupon_type.extension?.buy_count }) }}
                         </div>
                    </div>
                    
                    <!-- Right: Info -->
                    <div class="flex-1 p-3 flex flex-col justify-center">
                        <div class="font-bold text-gray-800 dark:text-gray-100 text-sm line-clamp-1">
                            {{ getCouponLabel(coupon) }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-300 mt-1">
                            {{ $t('Valid_Until') }}: {{ formatDate(coupon.expiry_time) }}
                        </div>
                    </div>
                    
                    <!-- Checkmark -->
                    <div class="absolute right-3 top-1/2 -translate-y-1/2">
                        <div v-if="selectedCouponId === coupon.id" class="w-5 h-5 rounded-full bg-brand flex items-center justify-center">
                            <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div v-else class="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                    </div>
                </div>
             </div>
             
             <!-- Empty State -->
             <div v-if="availableCoupons.length === 0" class="py-8 text-center text-gray-400 dark:text-gray-300">
                {{ $t('No_Coupons_Available', 'No coupons available') }}
             </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Coverage Area Bottom Sheet -->
    <transition name="fade">
      <div
        v-if="showCoverageDialog"
        class="coverage-overlay fixed inset-0 z-40 bg-black/45"
        @click.self="closeCoverageDialog"
      ></div>
    </transition>
    <transition name="slide-up">
      <div
        v-if="showCoverageDialog"
        class="mobile-frame-fixed tg-bottom-sheet-anchor coverage-sheet-wrap fixed bottom-0 z-50"
      >
        <div class="tg-bottom-sheet-panel coverage-sheet" role="dialog" aria-modal="true">
          <div class="coverage-sheet-handle" aria-hidden="true"></div>

          <div class="coverage-sheet-header">
            <span class="coverage-header-icon" aria-hidden="true">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <div class="coverage-header-text">
              <h3 class="coverage-title">{{ $t('Package_Coverage_Title') }}</h3>
              <p class="coverage-subtitle">
                {{ $t('Package_Coverage_Subtitle', coverageAreaList.length, { count: coverageAreaList.length }) }}
              </p>
            </div>
          </div>

          <div class="coverage-sheet-body">
            <div v-if="showCoverageSearch" class="coverage-search-box">
              <svg class="coverage-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z" />
              </svg>
              <input
                v-model.trim="coverageSearchKeyword"
                type="text"
                class="coverage-search-input"
                :placeholder="$t('Package_Coverage_Search')"
              />
            </div>

            <div
              v-if="filteredCoverageAreaList.length > 0 && coverageLayoutMode === 'single'"
              class="coverage-list-wrap"
            >
              <div
                v-for="code in filteredCoverageAreaList"
                :key="code"
                class="coverage-region-item"
              >
                <span class="coverage-region-flag-box">
                  <span
                    :class="`flag-icon flag-icon-${getFlagCode(code).toLowerCase()}`"
                    class="coverage-region-flag"
                    role="img"
                    :aria-label="displayCountryName(code)"
                  ></span>
                </span>
                <span class="coverage-region-name">{{ displayCountryName(code) }}</span>
              </div>
            </div>

            <div
              v-else-if="filteredCoverageAreaList.length > 0"
              class="coverage-compact-list coverage-scroll"
            >
              <div
                v-for="code in filteredCoverageAreaList"
                :key="code"
                class="coverage-compact-item"
              >
                <span class="coverage-region-flag-box coverage-region-flag-box--compact">
                  <span
                    :class="`flag-icon flag-icon-${getFlagCode(code).toLowerCase()}`"
                    class="coverage-region-flag"
                    role="img"
                    :aria-label="displayCountryName(code)"
                  ></span>
                </span>
                <span class="coverage-compact-name">{{ displayCountryName(code) }}</span>
              </div>
            </div>

            <div v-else class="coverage-empty">
              {{ $t('Package_Coverage_Empty') }}
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Package Detail Dialog -->
    <transition name="fade">
      <div
        v-if="showDetailDialog"
        class="package-detail-overlay fixed inset-0 bg-black/40 z-40"
        @click.self="closeDetailDialog"
      ></div>
    </transition>
    <transition name="slide-up">
      <div v-if="showDetailDialog" class="package-detail-sheet-wrap mobile-frame-fixed tg-bottom-sheet-anchor fixed bottom-0 z-50">
        <div class="package-detail-sheet tg-bottom-sheet-panel bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl border-t border-gray-100 dark:border-gray-800 max-h-[85vh] w-full flex flex-col overflow-hidden">
          <!-- Header: fixed outside scroll area -->
          <div class="package-detail-header flex-shrink-0 bg-white dark:bg-gray-900 rounded-t-3xl z-10 flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-800">
            <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ $t('Package_Detail_Title') }}</h3>
            <button
              @click="closeDetailDialog"
              class="package-detail-close w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-700 active:scale-95 transition-all"
            >
              <svg class="w-4 h-4 text-gray-500 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="package-detail-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden tg-sheet-scroll">
          <div class="package-detail-body px-5 pb-8">
            <!-- Country Flag and Name -->
            <div class="flex flex-col items-center py-5">
              <div class="relative mb-3">
                <div 
                  :class="`flag-icon flag-icon-${getFlagCode(countryCode).toLowerCase()}`" 
                  class="w-20 h-16 rounded-xl shadow-lg border-2 border-gray-100 overflow-hidden bg-gray-50 dark:bg-gray-900"
                  style="background-size: contain; background-position: center; background-repeat: no-repeat;"
                ></div>
                <div 
                  v-if="flagError"
                  class="absolute inset-0 w-20 h-16 rounded-xl shadow-lg border-2 border-gray-100 bg-gradient-to-br from-brand to-brand/80 flex items-center justify-center"
                >
                  <span class="text-white text-xs font-bold">Global</span>
                </div>
              </div>
              <h4 class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ displayCountryName(countryCode) }}</h4>
            </div>

            <!-- Plan Info: Validity & Price -->
            <div v-if="detailPlan" class="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl mb-5">
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500 dark:text-gray-300 font-medium">{{ $t('Package_Detail_Validity') }}</span>
                <span class="text-sm font-bold text-gray-900 dark:text-gray-100">{{ $t('Package_Detail_Days_Format', { count: detailPlan.ValidityDays, days: detailPlan.ValidityDays }) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-500 dark:text-gray-300 font-medium">{{ $t('Package_Detail_Price') }}</span>
                <span class="text-lg font-extrabold text-brand">${{ detailPlan.PriceUSD }}</span>
              </div>
            </div>

            <NetworkOperatorsPanel
              v-if="detailPlan?.ProductID"
              :key="`${detailPlan.ProductID}-${locale}`"
              :product-id="detailPlan.ProductID"
              class="mb-5"
            />

            <!-- Description List - eSIM -->
            <ul v-if="!isSim" class="space-y-3 mb-5">
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_eSIM_Desc_1') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_eSIM_Desc_2') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_eSIM_Desc_3') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_eSIM_Desc_4') }}</span>
              </li>
            </ul>

            <!-- Description List - SIM -->
            <ul v-else class="space-y-3 mb-5">
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_SIM_Desc_1') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_SIM_Desc_2') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_SIM_Desc_3') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_SIM_Desc_4') }}</span>
              </li>
              <li class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span class="text-gray-400 dark:text-gray-400 mt-1 flex-shrink-0">•</span>
                <span>{{ $t('Package_Detail_SIM_Desc_5') }}</span>
              </li>
            </ul>

            <!-- Warning Note -->
            <div class="bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/50 rounded-xl px-4 py-3">
              <p class="text-xs text-red-600 dark:text-red-300 leading-relaxed">
                {{ isSim ? $t('Package_Detail_SIM_Warning') : $t('Package_Detail_eSIM_Warning') }}
              </p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onActivated, onDeactivated, onBeforeUnmount, inject, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { showAppNotification } from '@/utils/appNotification'
import { useRouter, useRoute } from 'vue-router'
import { trackBeginCheckout } from '@/tracking'
import { getCountryRegionDisplayName, getFlagCountryCode } from '@/utils/countryRegionDisplay'
import { resetPageScroll } from '@/utils/scrollReset'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'
import { useBackButton } from '@/composables/useBackButton'
import {
  executeOrderPayment,
  estimateStarsFromUsd,
  getDefaultPaymentMethod,
  getAvailablePaymentMethods,
  getAixPaymentMethodLabelKey,
  isAixPaymentMethod,
  PAYMENT_METHOD_COBO,
  PAYMENT_METHOD_STARS,
  refreshAvailablePaymentMethods,
  shouldShowPaymentMethodPicker,
} from '@/utils/orderPaymentFlow'
import { estimateRubFromUsd, getCachedAixPayConfig } from '@/utils/aixpayPayment'
import { syncTelegramBackButton } from '@/utils/telegramBackButton'
import logger from '@/utils/logger'
import { resolveApiErrorMessage } from '@/utils/apiResponse'
import NetworkOperatorsPanel from '@/components/NetworkOperatorsPanel.vue'

const plansScrollRef = ref(null)
const PACKAGE_PAGE_BODY_CLASS = 'package-page-active'

const selectedPlan = ref(0)
const selectedDataKey = ref(null)
const selectedDays = ref(null)
const flagError = ref(false)
const coverageAreaList = ref([])
const showCoverageDialog = ref(false)
const coverageLoading = ref(false)
const coverageSearchKeyword = ref('')
const showDetailDialog = ref(false)
const detailPlanIndex = ref(null)
const detailPlan = computed(() => {
    if (detailPlanIndex.value === null || !planList.value) return null
    return planList.value[detailPlanIndex.value] || null
})

const selectPlan = (index) => {
    hapticLight()
    selectedPlan.value = index
}

const router = useRouter()
const route = useRoute()
const api = inject('api')
const { t, te, locale } = useI18n()
const typeFromQuery = Array.isArray(route.query.type) ? route.query.type[0] : route.query.type
const packageType = ref(typeFromQuery ? typeFromQuery : 'eSIM')
const isSim = computed(() => String(packageType.value ? packageType.value : '').toUpperCase() === 'SIM')
const showPaySheet = ref(false)
const paySheetBodyRef = ref(null)
const discountInputRef = ref(null)
const PAY_SHEET_KEYBOARD_BODY_CLASS = 'pay-sheet-keyboard-open'
let paySheetViewportBound = false
let paySheetBlurTimer = null
let paySheetViewportRaf = 0
const defaultAddress = ref(null)
const quantity = ref(1)
const agreeReturnPolicy = ref(false)
const selectedPaymentMethod = ref(getDefaultPaymentMethod())
const availablePaymentMethods = ref(getAvailablePaymentMethods())
const aixRubExchangeRate = ref(null)
const showPaymentMethodPicker = computed(
  () => shouldShowPaymentMethodPicker(availablePaymentMethods.value),
)
const isAixPaySheetSelected = computed(() => isAixPaymentMethod(selectedPaymentMethod.value))
const estimatedStarsAmount = computed(() => estimateStarsFromUsd(finalPrice.value))

const getPaymentMethodTitle = (method) => {
  if (method === PAYMENT_METHOD_STARS) return t('Payment_Method_Stars')
  if (method === PAYMENT_METHOD_COBO) return t('Payment_Method_Cobo')
  if (isAixPaymentMethod(method)) return t(getAixPaymentMethodLabelKey(method))
  return method
}

const getPaymentMethodDesc = (method) => {
  if (method === PAYMENT_METHOD_STARS) {
    return t('Payment_Method_Stars_Desc', { stars: estimatedStarsAmount.value || '—' })
  }
  if (method === PAYMENT_METHOD_COBO) return t('Payment_Method_Cobo_Desc')
  if (isAixPaymentMethod(method)) return t('Payment_Method_Aix_Desc')
  return ''
}
const addressListKey = 'addressList'
const selectedAddressKey = 'selectedAddress'
const deliveryMethodKey = 'deliveryMethod'
const deliveryMethod = ref(null)
const availableCoupons = ref([])
const selectedCouponId = ref(null)
const orderPriceInfo = ref(null)
const priceLoading = ref(false)
const couponLoading = ref(false)
const showCouponSelector = ref(false)
const discountCodeInput = ref('')
const appliedDiscountCode = ref(null)
const discountCodeApplying = ref(false)
const isPaying = ref(false)
const paymentResultKey = 'paymentResult'

const countryCode = ref(route.params.countryCode)

// 根据类型选择对应的产品列表存储键
const storageKey = isSim.value ? 'simProductList' : 'productList'
const productListString = localStorage.getItem(storageKey)

let productList = []
if (productListString) {
    productList = JSON.parse(productListString)
}

const planList = ref(productList[countryCode.value] || [])

const getDataKey = (plan) => {
    const gb = plan?.GB
    const gbKey = gb == null ? 'unlimited' : String(gb)
    return `${plan?.PlanType ?? 0}:${gbKey}`
}

const formatGbAmount = (gb) => {
    if (gb == null) return ''
    if (gb < 1) return `${Math.round(gb * 1024)}MB`
    return gb % 1 === 0 ? `${gb}GB` : `${gb.toFixed(1)}GB`
}

const formatDataLabel = (plan) => {
    const badge = plan?.Badge || plan?.BadgeZHCN
    if (badge) return badge
    const remarks = plan?.Remarks || plan?.RemarksZHCN
    if (remarks && String(remarks).length <= 40) return remarks

    const gb = plan?.GB
    if (gb == null) return t('Package_Unlimited_Data')

    if (Number(plan?.PlanType) === 1) {
        return `${formatGbAmount(gb)}/${t('Package_Per_Day')}`
    }
    return t('Package_Total_Data_Format', { gb: formatGbAmount(gb) })
}

const buildProductName = (plan) => {
    const parts = [displayCountryName(countryCode)]
    if (plan?.GB != null) {
        parts.push(`${plan.GB}GB`)
    }
    if (plan?.ValidityDays != null) {
        parts.push(`${plan.ValidityDays}Days`)
    }
    parts.push('eSIM')
    return parts.join(' ')
}

const dataOptions = computed(() => {
    const seen = new Map()
    ;(planList.value || []).forEach((plan) => {
        const key = getDataKey(plan)
        if (seen.has(key)) return
        const label = formatDataLabel(plan)
        seen.set(key, {
            key,
            label,
            fullWidth: label.length > 16 || !!(plan?.Remarks || plan?.RemarksZHCN)
        })
    })
    return Array.from(seen.values())
})

const dayOptions = computed(() => {
    const days = [...new Set((planList.value || []).map((plan) => plan.ValidityDays))]
    return days.sort((a, b) => a - b)
})

const planLookup = computed(() => {
    const map = {}
    ;(planList.value || []).forEach((plan, index) => {
        map[`${getDataKey(plan)}:${plan.ValidityDays}`] = index
    })
    return map
})

const isComboAvailable = (dataKey, days) => {
    if (!dataKey || days == null) return false
    return planLookup.value[`${dataKey}:${days}`] !== undefined
}

const isDataOptionAvailable = (dataKey) => {
    if (!dataKey) return false
    return dayOptions.value.some((days) => isComboAvailable(dataKey, days))
}

const availableDayOptions = computed(() => {
    if (!selectedDataKey.value) return []
    return dayOptions.value.filter((days) => isComboAvailable(selectedDataKey.value, days))
})

const showDaySelector = computed(() => availableDayOptions.value.length > 1)

const singleDayDisplay = computed(() => {
    if (availableDayOptions.value.length !== 1) return null
    return availableDayOptions.value[0]
})

const syncSelectedPlan = () => {
    const idx = planLookup.value[`${selectedDataKey.value}:${selectedDays.value}`]
    if (idx !== undefined) {
        selectedPlan.value = idx
    }
}

const initSimSelection = () => {
    if (!isSim.value || !planList.value?.length) return
    const firstPlan = planList.value[0]
    selectedDataKey.value = getDataKey(firstPlan)
    selectedDays.value = firstPlan.ValidityDays
    syncSelectedPlan()
}

const selectDataOption = (dataKey) => {
    if (!isDataOptionAvailable(dataKey)) return
    hapticLight()
    selectedDataKey.value = dataKey
    if (!isComboAvailable(dataKey, selectedDays.value)) {
        const firstAvailableDay = dayOptions.value.find((days) => isComboAvailable(dataKey, days))
        selectedDays.value = firstAvailableDay ?? null
    }
    syncSelectedPlan()
}

const selectDaysOption = (days) => {
    if (!isComboAvailable(selectedDataKey.value, days)) return
    hapticLight()
    selectedDays.value = days
    syncSelectedPlan()
}

const displayPrice = computed(() => {
    if (!currentPlan.value) return '--'
    return Number(currentPlan.value.PriceUSD).toFixed(2)
})

const formatPlanPrice = (price) => {
    const n = Number(price)
    if (!Number.isFinite(n)) return '--'
    return n.toFixed(2)
}

const formatPlanUnitPrice = (plan) => {
    const gb = Number(plan?.GB)
    const price = Number(plan?.PriceUSD)
    if (!gb || !price) return '--'
    return (price / gb).toFixed(2)
}

const selectedDataLabel = computed(() => {
    const opt = dataOptions.value.find((o) => o.key === selectedDataKey.value)
    return opt?.label || ''
})

const currentPlan = computed(() => {
    if (!planList.value) {
        return null
    }
    return planList.value[selectedPlan.value] ? planList.value[selectedPlan.value] : null
})

const {
  hapticFeedback,
  hideKeyboard,
  enableClosingConfirmation,
  disableClosingConfirmation,
} = useTelegramWebApp()
const { setupBackButton } = useBackButton({ autoSetup: false })

const hapticLight = () => {
    hapticFeedback('impact', 'light')
}

const deliveryMethodText = computed(() => {
    const method = deliveryMethod.value
    if (!method) {
        return t('Package_Shipping_Free_Default')
    }
    if (Number(method.price) === 0) {
        return t('Package_Shipping_Free_Format', { name: method.name })
    }
    return t('Package_Shipping_Price_Format', { name: method.name, price: method.price })
})

const handleFlagError = () => {
    flagError.value = true
}

const getFlagCode = (codeRef) => {
    const code = typeof codeRef === 'string' ? codeRef : codeRef?.value
    return getFlagCountryCode(code)
}

const sortCoverageCodes = (codes) => {
    return [...codes].sort((a, b) =>
        displayCountryName(a).localeCompare(displayCountryName(b), undefined, { sensitivity: 'base' })
    )
}

const filteredCoverageAreaList = computed(() => {
    const keyword = (coverageSearchKeyword.value || '').trim().toLowerCase()
    const list = !keyword
        ? coverageAreaList.value
        : coverageAreaList.value.filter((code) => {
            const name = displayCountryName(code).toLowerCase()
            return code.toLowerCase().includes(keyword) || name.includes(keyword)
        })
    return sortCoverageCodes(list)
})

/** single: 1 region | compact: 2+ */
const coverageLayoutMode = computed(() => {
    const total = coverageAreaList.value.length
    if (total <= 1) return 'single'
    return 'compact'
})

const showCoverageSearch = computed(() => coverageAreaList.value.length >= 9)

const openCoverageDialog = () => {
    coverageSearchKeyword.value = ''
    showCoverageDialog.value = true
}

const closeCoverageDialog = () => {
    showCoverageDialog.value = false
}

const loadCoverageArea = async () => {
    if (coverageLoading.value) return
    coverageLoading.value = true
    try {
        const res = await api.product.getCoverageArea(countryCode.value)
        if (res?.data?.status === '0' && res?.data?.data) {
            // CoverageArea is a comma-separated string of country codes
            const coverageStr = res.data.data
            if (coverageStr && typeof coverageStr === 'string') {
                coverageAreaList.value = coverageStr.split(',').map(code => code.trim()).filter(code => code)
            }
        }
    } catch (error) {
        logger.warn('Failed to load coverage area:', error)
    } finally {
        coverageLoading.value = false
    }
}

const loadSelectedAddress = () => {
    const selectedStr = localStorage.getItem(selectedAddressKey)
    if (selectedStr) {
        try {
            defaultAddress.value = JSON.parse(selectedStr)
            return
        } catch (error) {
            logger.warn('Failed to parse selected address:', error)
        }
    }
    const listStr = localStorage.getItem(addressListKey)
    if (!listStr) {
        defaultAddress.value = null
        return
    }
    try {
        const list = JSON.parse(listStr)
        const fallback = list.find((item) => item.isDefault)
        defaultAddress.value = fallback ? fallback : null
    } catch (error) {
        logger.warn('Failed to parse address list:', error)
        defaultAddress.value = null
    }
}

const loadDeliveryMethod = () => {
    const stored = localStorage.getItem(deliveryMethodKey)
    if (stored) {
        try {
            deliveryMethod.value = JSON.parse(stored)
            return
        } catch (error) {
            logger.warn('Failed to parse delivery method:', error)
        }
    }
    deliveryMethod.value = { id: 'standard', name: t('Package_Shipping_Standard'), price: 0 }
}

const fetchDefaultAddress = async () => {
    // TODO: Replace with real default address API call
    loadSelectedAddress()
}

const increaseQty = () => {
    quantity.value += 1
}

const decreaseQty = () => {
    if (quantity.value > 1) {
        quantity.value -= 1
    }
}

const loadCoupons = async () => {
    if (couponLoading.value) return
    couponLoading.value = true
    try {
        const res = await api.coupon.getUserCouponList(1) // status=1 for available
        if (res?.data?.status === '0' && res?.data?.data) {
            availableCoupons.value = res.data.data
        }
    } catch (error) {
        logger.warn('Failed to load coupons:', error)
    } finally {
        couponLoading.value = false
    }
}

const fetchPrice = async () => {
    if (!currentPlan.value || !currentPlan.value.ProductID) return
    
    priceLoading.value = true
    try {
        const params = {
            product_id: currentPlan.value.ProductID,
            quantity: quantity.value
        }
        if (appliedDiscountCode.value) {
            params.discount_code = appliedDiscountCode.value
        } else if (selectedCouponId.value) {
            params.coupon_id = selectedCouponId.value
        }
        
        const res = await api.order.getPrice(params)
        if (res?.data?.status === '0' && res?.data?.data) {
            // Merge res.data.data with other properties in res.data (like CouponPriceUSD, CouponList)
            // Extract data, status, message to avoid duplication or overwriting
            const { data, status, message, ...rest } = res.data
            orderPriceInfo.value = { ...data, ...rest }
        } else {
            orderPriceInfo.value = null
        }
    } catch (error) {
        logger.warn('Failed to fetch price:', error)
        orderPriceInfo.value = null
    } finally {
        priceLoading.value = false
    }
}

const openCouponSelector = () => {
    if (appliedDiscountCode.value) return
    showCouponSelector.value = true
}

const applyDiscountCode = async () => {
    const code = (discountCodeInput.value || '').trim().toUpperCase()
    if (!code || !currentPlan.value?.ProductID) return
    if (selectedCouponId.value) {
        showAppNotification({
            message: t('Discount_Code_Exclusive_With_Coupon'),
            type: 'warning',
            duration: 2500,
            position: 'bottom-right'
        })
        return
    }
    discountCodeApplying.value = true
    try {
        const res = await api.discountCode.validate(code, currentPlan.value.ProductID)
        if (res?.data?.status !== '0') {
            showAppNotification({
                message: resolveApiErrorMessage(res?.data, t('Discount_Code_Invalid')),
                type: 'error',
                duration: 2500,
                position: 'bottom-right'
            })
            return
        }
        appliedDiscountCode.value = code
        discountCodeInput.value = ''
        selectedCouponId.value = null
        await fetchPrice()
        showAppNotification({
            message: t('Discount_Applied'),
            type: 'success',
            duration: 2000,
            position: 'bottom-right'
        })
    } catch (error) {
        showAppNotification({
            message: resolveApiErrorMessage(error?.response?.data, t('Discount_Code_Invalid')),
            type: 'error',
            duration: 2500,
            position: 'bottom-right'
        })
    } finally {
        discountCodeApplying.value = false
        hideKeyboard()
    }
}

const clearDiscountCode = () => {
    appliedDiscountCode.value = null
    discountCodeInput.value = ''
    fetchPrice()
}

const closeCouponSelector = () => {
    showCouponSelector.value = false
    syncPackageBackButton()
}

const selectCoupon = (couponId) => {
    if (appliedDiscountCode.value) {
        showAppNotification({
            message: t('Discount_Code_Exclusive_With_Coupon'),
            type: 'warning',
            duration: 2500,
            position: 'bottom-right'
        })
        return
    }
    selectedCouponId.value = couponId
    fetchPrice()
    closeCouponSelector()
}

const formatDiscount = (rate) => {
    if (!rate) return ''
    const isChinese = locale.value === 'zh' || locale.value === 'zh-CN' || locale.value === 'zh-HK' || locale.value === 'zh-TW'
    if (isChinese) {
        const val = rate * 10
        const displayVal = val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)
        return `${displayVal}折`
    } else {
        const percent = Math.round((1 - rate) * 100)
        return `${percent}%`
    }
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString()
    } catch (e) {
        return dateStr
    }
}

const onCouponChange = () => {
    fetchPrice()
}

const getCouponLabel = (coupon) => {
    const type = coupon.coupon_type?.type
    if (type === 1) {
        // Discount
        const rate = coupon.coupon_type.discount_rate
        const percent = Math.round((1 - rate) * 100)
        return `${percent}% OFF`
    } else if (type === 2) {
        // Cash
        return `$${coupon.coupon_type.cash_value} OFF`
    } else if (type === 3) {
        // Reduction
        return `-$${coupon.coupon_type.reduction_amount}`
    } else if (type === 4) {
        // Discount on items
        return `Buy ${coupon.coupon_type.extension?.buy_count} items Discount`
    }
    return coupon.coupon_type?.name || t('Coupon')
}

// Compute final price to display
const finalPrice = computed(() => {
    // If we have price info from API
    if (orderPriceInfo.value) {
        if (appliedDiscountCode.value && orderPriceInfo.value.CouponPriceUSD != null) {
            return Number(orderPriceInfo.value.CouponPriceUSD).toFixed(2)
        }
        // 1. If a coupon is selected, try to find it in the CouponList from the price response
        if (selectedCouponId.value && Array.isArray(orderPriceInfo.value.CouponList)) {
            // Use loose comparison (==) as ID might be number or string
            const appliedCoupon = orderPriceInfo.value.CouponList.find(c => c.CouponId == selectedCouponId.value)
            if (appliedCoupon && appliedCoupon.CouponPriceUSD !== undefined && appliedCoupon.CouponPriceUSD !== null) {
                return Number(appliedCoupon.CouponPriceUSD).toFixed(2)
            }
        }

        // 2. Use root CouponPriceUSD if available (price after coupon)
        if (orderPriceInfo.value.CouponPriceUSD !== undefined && orderPriceInfo.value.CouponPriceUSD !== null) {
            return Number(orderPriceInfo.value.CouponPriceUSD).toFixed(2)
        }
        
        // 3. Fallback to OriginalPriceUSD if no coupon applied or invalid coupon
        if (orderPriceInfo.value.OriginalPriceUSD !== undefined) {
            return Number(orderPriceInfo.value.OriginalPriceUSD).toFixed(2)
        }
    }
    
    // Default calculation (local fallback)
    if (currentPlan.value) {
        return (currentPlan.value.PriceUSD * quantity.value).toFixed(2)
    }
    return '0.00'
})

// Compute discount amount to display
const discountAmount = computed(() => {
    if (orderPriceInfo.value) {
        if (appliedDiscountCode.value && orderPriceInfo.value.DiscountPriceUSD) {
            return Number(orderPriceInfo.value.DiscountPriceUSD).toFixed(2)
        }
        // 1. If a coupon is selected, try to find it in the CouponList from the price response
        if (selectedCouponId.value && Array.isArray(orderPriceInfo.value.CouponList)) {
            const appliedCoupon = orderPriceInfo.value.CouponList.find(c => c.CouponId == selectedCouponId.value)
            if (appliedCoupon && appliedCoupon.DiscountPriceUSD !== undefined && appliedCoupon.DiscountPriceUSD !== null) {
                return Number(appliedCoupon.DiscountPriceUSD).toFixed(2)
            }
        }

        // 2. Use root DiscountPriceUSD if available
        if (orderPriceInfo.value.DiscountPriceUSD) {
            return Number(orderPriceInfo.value.DiscountPriceUSD).toFixed(2)
        }
    }
    return '0.00'
})

const paySheetOriginalPrice = computed(() => {
    if (orderPriceInfo.value?.OriginalPriceUSD != null) {
        return Number(orderPriceInfo.value.OriginalPriceUSD).toFixed(2)
    }
    if (currentPlan.value) {
        return (currentPlan.value.PriceUSD * quantity.value).toFixed(2)
    }
    return '0.00'
})

const finalPriceRub = computed(() => {
    if (!isAixPaySheetSelected.value) return null
    return estimateRubFromUsd(finalPrice.value, aixRubExchangeRate.value)
})

const paySheetOriginalPriceRub = computed(() => {
    if (!isAixPaySheetSelected.value) return null
    return estimateRubFromUsd(paySheetOriginalPrice.value, aixRubExchangeRate.value)
})

const formatPaySheetPrice = (usdAmount, rubAmount) => {
    if (isAixPaySheetSelected.value && rubAmount) {
        return t('Payment_Rub_Amount_Format', { amount: rubAmount })
    }
    return `$${usdAmount}`
}

const KEYBOARD_INSET_THRESHOLD = 72

const getKeyboardInset = () => {
    const vv = window.visualViewport
    if (!vv) return 0
    return Math.max(0, window.innerHeight - (vv.offsetTop + vv.height))
}

const clearPaySheetKeyboardLayout = () => {
    document.documentElement.style.removeProperty('--pay-sheet-keyboard-inset')
    document.documentElement.style.removeProperty('--pay-sheet-panel-max-height')
    document.body.classList.remove(PAY_SHEET_KEYBOARD_BODY_CLASS)
}

const applyPaySheetKeyboardLayout = (keyboardInset, panelMaxHeight) => {
    document.documentElement.style.setProperty('--pay-sheet-keyboard-inset', `${keyboardInset}px`)
    document.documentElement.style.setProperty('--pay-sheet-panel-max-height', `${panelMaxHeight}px`)
    document.body.classList.add(PAY_SHEET_KEYBOARD_BODY_CLASS)
}

const updatePaySheetViewport = () => {
    if (!showPaySheet.value) {
        clearPaySheetKeyboardLayout()
        return
    }

    const vv = window.visualViewport
    if (!vv) {
        clearPaySheetKeyboardLayout()
        return
    }

    const keyboardInset = getKeyboardInset()

    if (keyboardInset > KEYBOARD_INSET_THRESHOLD) {
        applyPaySheetKeyboardLayout(
            keyboardInset,
            Math.min(Math.round(vv.height * 0.92), 520),
        )
        return
    }

    clearPaySheetKeyboardLayout()
}

const scrollPaySheetInputIntoView = () => {
    nextTick(() => {
        const body = paySheetBodyRef.value
        const input = discountInputRef.value
        if (!body || !input) return

        const vv = window.visualViewport
        const inputRect = input.getBoundingClientRect()
        const bodyRect = body.getBoundingClientRect()
        const safeBottom = vv
            ? vv.offsetTop + vv.height - 16
            : window.innerHeight - 16

        if (inputRect.bottom > safeBottom) {
            body.scrollTop += inputRect.bottom - safeBottom
            return
        }

        const overflow = inputRect.bottom - bodyRect.bottom + 12
        if (overflow > 0) {
            body.scrollTop += overflow
        }
    })
}

const onPaySheetViewportChange = () => {
    if (paySheetViewportRaf) {
        cancelAnimationFrame(paySheetViewportRaf)
    }
    paySheetViewportRaf = requestAnimationFrame(() => {
        paySheetViewportRaf = 0
        updatePaySheetViewport()
    })
}

const bindPaySheetViewport = () => {
    if (paySheetViewportBound || !window.visualViewport) return
    paySheetViewportBound = true
    window.visualViewport.addEventListener('resize', onPaySheetViewportChange)
    window.visualViewport.addEventListener('scroll', onPaySheetViewportChange)
}

const unbindPaySheetViewport = () => {
    if (!paySheetViewportBound || !window.visualViewport) return
    paySheetViewportBound = false
    window.visualViewport.removeEventListener('resize', onPaySheetViewportChange)
    window.visualViewport.removeEventListener('scroll', onPaySheetViewportChange)
}

const schedulePaySheetKeyboardSync = () => {
    onPaySheetViewportChange()
    requestAnimationFrame(() => {
        updatePaySheetViewport()
        scrollPaySheetInputIntoView()
    })
    setTimeout(() => {
        updatePaySheetViewport()
        scrollPaySheetInputIntoView()
    }, 280)
}

const onPaySheetInputFocus = () => {
    if (paySheetBlurTimer) {
        clearTimeout(paySheetBlurTimer)
        paySheetBlurTimer = null
    }
    document.body.classList.add(PAY_SHEET_KEYBOARD_BODY_CLASS)
    schedulePaySheetKeyboardSync()
}

const onPaySheetInputBlur = () => {
    hideKeyboard()
    paySheetBlurTimer = setTimeout(() => {
        paySheetBlurTimer = null
        updatePaySheetViewport()
    }, 180)
}

const resetPaySheetKeyboardState = () => {
    if (paySheetBlurTimer) {
        clearTimeout(paySheetBlurTimer)
        paySheetBlurTimer = null
    }
    if (paySheetViewportRaf) {
        cancelAnimationFrame(paySheetViewportRaf)
        paySheetViewportRaf = 0
    }
    clearPaySheetKeyboardLayout()
    unbindPaySheetViewport()
}

const openPaySheet = async () => {
    if (selectedPlan.value === null) {
        return
    }
    if (isSim.value) {
        await fetchDefaultAddress()
    }
    
    // Reset coupon / discount code selection
    selectedCouponId.value = null
    appliedDiscountCode.value = null
    discountCodeInput.value = ''
    orderPriceInfo.value = null
    await refreshAvailablePaymentMethods(api, { locale: locale.value })
    availablePaymentMethods.value = getAvailablePaymentMethods()
    aixRubExchangeRate.value = getCachedAixPayConfig()?.rubExchangeRate ?? null
    selectedPaymentMethod.value = getDefaultPaymentMethod()
    
    showPaySheet.value = true
    bindPaySheetViewport()
    nextTick(() => {
        updatePaySheetViewport()
        syncPackageBackButton()
    })
    
    // Load coupons and initial price
    loadCoupons()
    fetchPrice()
}

const closePaySheet = () => {
    // 关闭时重置弹窗内状态，下次打开不保留
    discountInputRef.value?.blur()
    hideKeyboard()
    isPaying.value = false
    agreeReturnPolicy.value = false
    selectedPaymentMethod.value = getDefaultPaymentMethod()
    quantity.value = 1
    showPaySheet.value = false
    resetPaySheetKeyboardState()
    syncPackageBackButton()
}

const syncPackageBackButton = () => {
    if (showCouponSelector.value) {
        setupBackButton(true, closeCouponSelector)
        return
    }
    if (showPaySheet.value) {
        setupBackButton(true, closePaySheet)
        return
    }
    syncTelegramBackButton(route, router)
}

const confirmPay = async () => {
    if (isPaying.value) {
        return
    }
    if (!agreeReturnPolicy.value) {
        showAppNotification({
            message: t('Package_Return_Policy_Required'),
            type: 'warning',
            duration: 2500,
            position: 'bottom-right'
        })
        return
    }
    // if (isSim.value && !defaultAddress.value) {
    //     showAppNotification({
    //         message: '请先填写收货人信息',
    //         type: 'warning',
    //         duration: 3000,
    //         position: 'top-right'
    //     })
    //     return
    // }
    if (!currentPlan.value || !currentPlan.value.ProductID) {
        showAppNotification({
            message: t('Package_Info_Incomplete'),
            type: 'warning',
            duration: 2500,
            position: 'bottom-right'
        })
        return
    }
    
    // GTM：开始结算/交易事件（点击确认支付时触发）
    const productName = buildProductName(currentPlan.value)
    trackBeginCheckout({
      productId: currentPlan.value.ProductID,
      productName,
      priceUSD: currentPlan.value.PriceUSD,
      totalUSD: currentPlan.value.PriceUSD * quantity.value,
      items: [{
        item_id: String(currentPlan.value.ProductID),
        item_name: productName,
        price: currentPlan.value.PriceUSD,
        quantity: quantity.value
      }]
    })
    
    isPaying.value = true
    try {
        const payload = {
            NotificationEmail: 'xxx@qq.com',
            ProductId: currentPlan.value.ProductID
        }
        if (appliedDiscountCode.value) {
            payload.DiscountCode = appliedDiscountCode.value
        } else if (selectedCouponId.value) {
            payload.CouponId = selectedCouponId.value
        }

        const createRes = await api.order.createOrder(payload)
        const orderNo = createRes?.data?.data?.OrderNo
        if (!orderNo || createRes?.data?.status !== '0') {
            showAppNotification({
                message: resolveApiErrorMessage(createRes?.data, t('Package_Pay_Process_Failed')),
                type: 'error',
                duration: 2500,
                position: 'bottom-right'
            })
            return
        }

        const payResult = await executeOrderPayment({
            api,
            router,
            orderNo,
            productName,
            amountUsd: finalPrice.value,
            paymentMethod: selectedPaymentMethod.value,
            t,
        })

        if (!payResult.ok) {
            showAppNotification({
                message: payResult.message || t('Package_Pay_Process_Failed'),
                type: 'error',
                duration: 2500,
                position: 'bottom-right'
            })
            return
        }

        localStorage.removeItem(paymentResultKey)
        showPaySheet.value = false
        resetPaySheetKeyboardState()

        if (
            payResult.method === PAYMENT_METHOD_STARS
            && (payResult.invoiceStatus === 'cancelled' || payResult.invoiceStatus === 'failed')
        ) {
            showAppNotification({
                message: t('Payment_Failed_Message'),
                type: 'warning',
                duration: 2500,
                position: 'bottom-right'
            })
        }
    } catch (error) {
        logger.error('支付流程失败:', error)
        showAppNotification({
            message: resolveApiErrorMessage(error?.response?.data, t('Package_Pay_Process_Failed')),
            type: 'error',
            duration: 2500,
            position: 'bottom-right'
        })
    } finally {
        isPaying.value = false
    }
}

const openPlanDetail = (index) => {
    detailPlanIndex.value = index
    showDetailDialog.value = true
}

const closeDetailDialog = () => {
    showDetailDialog.value = false
    detailPlanIndex.value = null
}

const goToAddressPage = () => {
    router.push({ path: '/address' })
}

const goToDeliveryMethodPage = () => {
    router.push({ path: '/delivery-method' })
}

const goBack = () => {
    router.back()
}

watch(planList, () => {
    initSimSelection()
}, { immediate: true })

const lockPackagePageScroll = () => {
    document.body.classList.add(PACKAGE_PAGE_BODY_CLASS)
    document.documentElement.classList.add(PACKAGE_PAGE_BODY_CLASS)
}

const unlockPackagePageScroll = () => {
    document.body.classList.remove(PACKAGE_PAGE_BODY_CLASS)
    document.documentElement.classList.remove(PACKAGE_PAGE_BODY_CLASS)
}

const resetPlansScroll = () => {
    resetPageScroll()
    if (plansScrollRef.value) {
        plansScrollRef.value.scrollTop = 0
    }
}

onMounted(() => {
    lockPackagePageScroll()
    resetPlansScroll()
    loadSelectedAddress()
    loadDeliveryMethod()
    loadCoverageArea()
    initSimSelection()
    syncPackageBackButton()
})

onActivated(() => {
    lockPackagePageScroll()
    resetPlansScroll()
    loadSelectedAddress()
    loadDeliveryMethod()
    syncPackageBackButton()
})

onDeactivated(() => {
    unlockPackagePageScroll()
    syncTelegramBackButton(route, router)
})

onBeforeUnmount(() => {
    unlockPackagePageScroll()
    resetPaySheetKeyboardState()
    disableClosingConfirmation()
    syncTelegramBackButton(route, router)
})

watch(showPaySheet, (open) => {
    if (open) {
        enableClosingConfirmation()
    } else {
        disableClosingConfirmation()
    }
    syncPackageBackButton()
})

watch(showCouponSelector, () => {
    syncPackageBackButton()
})

watch(
    () => route.fullPath,
    () => {
        resetPlansScroll()
    }
)

const displayCountryName = (codeRef) => {
    const code = typeof codeRef === 'string' ? codeRef : codeRef.value
    return getCountryRegionDisplayName({
        code,
        locale: locale?.value,
        t,
        te
    })
}

</script>

<style scoped>
/* Custom flag icon styles */
.flag-icon {
  background-size: contain !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  min-width: 80px;
  min-height: 64px;
}

.flag-icon-small {
  min-width: 64px;
  min-height: 64px;
}

/* Bottom sheet transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.mobile-frame-fixed.slide-up-enter-from,
.mobile-frame-fixed.slide-up-leave-to {
  transform: translateX(-50%) translateY(100%);
}

body.tg-webapp .mobile-frame-fixed.slide-up-enter-from,
body.tg-webapp .mobile-frame-fixed.slide-up-leave-to {
  transform: translateY(100%);
}

body.tg-fullscreen .mobile-frame-fixed.slide-up-enter-from,
body.tg-fullscreen .mobile-frame-fixed.slide-up-leave-to {
  transform: translateY(100%);
}

/* Coupon selector sheet */
.coupon-selector-overlay {
  background: rgba(15, 23, 42, 0.34);
  backdrop-filter: blur(2px);
}

.coupon-selector-sheet-wrap {
  pointer-events: none;
}

.coupon-selector-sheet {
  pointer-events: auto;
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -18px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.coupon-selector-header {
  border-radius: 24px 24px 0 0;
  background: #ffffff;
}

.coupon-selector-body {
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* Dark mode: scoped white backgrounds above were overriding the Tailwind
   dark:bg-gray-900 classes on these sheets, leaving a white header strip. */
html.dark .coupon-selector-sheet,
html.dark .coupon-selector-header,
html.dark .package-detail-sheet {
  background: #111827;
}

:global(body.tg-fullscreen .coupon-selector-overlay) {
  background: rgba(15, 23, 42, 0.28);
}

:global(body.tg-fullscreen .coupon-selector-sheet-wrap) {
  padding-left: 0 !important;
  padding-right: 0 !important;
  background: transparent !important;
}

:global(body.tg-fullscreen .coupon-selector-sheet-wrap::after) {
  background: #f9fafb !important;
}

:global(html.dark body.tg-fullscreen .coupon-selector-sheet-wrap::after) {
  background: #111827 !important;
}

:global(body.tg-fullscreen .coupon-selector-sheet) {
  width: 100%;
  max-width: none;
  max-height: min(calc(var(--tg-viewport-height, 100dvh) - var(--tg-content-safe-top, 0px) - 10px), 520px) !important;
  border-radius: 28px 28px 0 0 !important;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 -12px 34px rgba(15, 23, 42, 0.12);
}

:global(body.tg-fullscreen .coupon-selector-header) {
  border-radius: 28px 28px 0 0 !important;
  padding: 22px 28px 14px !important;
  border-bottom-color: rgba(15, 23, 42, 0.06);
}

:global(body.tg-fullscreen .coupon-selector-body) {
  padding: 20px 28px calc(28px + max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px))) !important;
}

:global(body.tg-fullscreen .coupon-selector-close) {
  width: 44px !important;
  height: 44px !important;
  background: #f3f4f6 !important;
}

:global(html.dark body.tg-fullscreen .coupon-selector-close) {
  background: #1f2937 !important;
}

/* Plan detail sheet */
.package-detail-overlay {
  background: rgba(15, 23, 42, 0.34);
  backdrop-filter: blur(2px);
}

.package-detail-sheet-wrap {
  pointer-events: none;
}

.package-detail-sheet {
  pointer-events: auto;
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -18px 48px rgba(15, 23, 42, 0.18);
}

.package-detail-header {
  border-radius: 24px 24px 0 0;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.package-detail-scroll {
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}

.package-detail-close {
  color: #6b7280;
}

:global(body.tg-fullscreen .package-detail-overlay) {
  background: rgba(15, 23, 42, 0.28);
}

:global(body.tg-fullscreen .package-detail-sheet-wrap) {
  padding-left: 0 !important;
  padding-right: 0 !important;
  background: transparent !important;
}

:global(body.tg-fullscreen .package-detail-sheet-wrap::after) {
  background: #ffffff !important;
}

:global(html.dark body.tg-fullscreen .package-detail-sheet-wrap::after) {
  background: #111827 !important;
}

:global(body.tg-fullscreen .package-detail-sheet) {
  width: 100%;
  max-width: none;
  max-height: min(calc(var(--tg-viewport-height, 100dvh) - var(--tg-content-safe-top, 0px) - 10px), 620px) !important;
  border-radius: 28px 28px 0 0 !important;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 -12px 34px rgba(15, 23, 42, 0.12);
}

:global(body.tg-fullscreen .package-detail-header) {
  border-radius: 28px 28px 0 0 !important;
  padding: 22px 28px 14px !important;
  border-bottom-color: rgba(15, 23, 42, 0.06);
}

:global(body.tg-fullscreen .package-detail-body) {
  padding-left: 28px !important;
  padding-right: 28px !important;
  padding-bottom: calc(28px + max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px))) !important;
}

:global(body.tg-fullscreen .package-detail-close) {
  width: 44px !important;
  height: 44px !important;
  background: #f3f4f6 !important;
}

:global(html.dark body.tg-fullscreen .package-detail-close) {
  background: #1f2937 !important;
}

/* Package page layout */
.package-page {
  --package-checkout-bar-height: 112px;
  --package-checkout-scroll-gap: 20px;
  display: flex;
  flex-direction: column;
  height: var(--tg-viewport-height, 100dvh);
  max-height: var(--tg-viewport-height, 100dvh);
  overflow: hidden;
  box-sizing: border-box;
}

.package-page-header {
  box-shadow: none;
  flex-shrink: 0;
}

.package-country-section {
  flex-shrink: 0;
}

.package-plans-scroll {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0 16px;
  padding-bottom: calc(
    var(--package-checkout-bar-height) +
    var(--package-checkout-scroll-gap) +
    max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px))
  );
  scroll-padding-bottom: calc(
    var(--package-checkout-bar-height) +
    var(--package-checkout-scroll-gap) +
    max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px))
  );
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.package-plans-scroll::-webkit-scrollbar {
  width: 0;
  display: none;
}

.sim-plan-selector,
.package-esim-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 2px;
  padding-bottom: 12px;
}

/* Current selection summary */
.package-summary-card {
  padding: 14px 16px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #eef0f3;
  box-shadow: 0 1px 6px rgba(15, 23, 42, 0.04);
}

.package-summary-label {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.package-summary-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.package-summary-plan {
  margin: 0;
  font-size: 15px;
  line-height: 1.35;
  color: #374151;
}

.package-summary-plan-strong {
  font-weight: 700;
  color: #111827;
}

.package-summary-plan-dot {
  margin: 0 4px;
  color: #d1d5db;
}

.package-summary-empty {
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
}

.package-summary-sms {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 6px 0 0;
  font-size: 12px;
  color: #9ca3af;
}

.package-summary-detail-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  margin-top: 2px;
  font-size: 13px;
  font-weight: 500;
  color: #8b5cf6;
  background: transparent;
  border: none;
  white-space: nowrap;
  transition: color 0.2s ease, opacity 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.package-summary-detail-link:active {
  opacity: 0.7;
}

.sim-section-card {
  padding: 16px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #eef0f3;
  box-shadow: 0 1px 6px rgba(15, 23, 42, 0.04);
}

.sim-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.sim-section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
}

.sim-section-icon--data {
  background: rgba(139, 92, 246, 0.1);
  color: #7c3aed;
}

.sim-section-icon--days {
  background: rgba(99, 102, 241, 0.08);
  color: #6366f1;
}

.sim-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.sim-data-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.sim-days-info {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  line-height: 1.4;
}

.sim-days-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

@media (min-width: 400px) {
  .sim-days-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }
}

.sim-option-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.sim-option-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.sim-option-label {
  padding: 0;
}

.sim-option-check-inline {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #7c3aed;
}

.sim-option-btn--default {
  background: #f8f9fb;
  border: 1px solid #e8eaef;
  color: #374151;
}

.sim-option-btn--default:not(:disabled):active {
  transform: scale(0.97);
  background: #f3f4f6;
}

.sim-option-btn--active {
  background: rgba(139, 92, 246, 0.05);
  border: 1.5px solid #8b5cf6;
  color: #6d28d9;
  box-shadow: 0 1px 4px rgba(139, 92, 246, 0.08);
}

.sim-option-btn--disabled {
  background: #f9fafb;
  border: 1.5px solid #f3f4f6;
  color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.85;
}

.sim-option-strike {
  position: relative;
  display: inline-block;
}

.sim-option-strike::after {
  content: '';
  position: absolute;
  left: -6%;
  right: -6%;
  top: 50%;
  height: 1.5px;
  background: #d1d5db;
  transform: rotate(-10deg);
}

.sim-day-btn {
  min-height: 46px;
}

.sim-day-label {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  line-height: 1.2;
}

.sim-day-num {
  font-size: 16px;
  font-weight: 700;
}

.sim-day-unit {
  font-size: 14px;
  font-weight: 500;
  color: inherit;
  opacity: 0.75;
}

.sim-option-btn--active .sim-day-unit {
  opacity: 0.9;
}

/* Country hero */
.package-hero-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #eef0f3;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  margin-bottom: 0;
}

.package-hero-main {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
}

.package-hero-flag {
  position: relative;
  flex-shrink: 0;
  width: 60px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(145deg, #f8f7fc 0%, #f3f4f6 100%);
  border: 1px solid #e8eaef;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.package-hero-flag-img {
  width: 100%;
  height: 100%;
  min-height: 44px;
}

.package-hero-flag-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.package-hero-text {
  flex: 1;
  min-width: 0;
}

.package-hero-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: #111827;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.package-hero-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 4px 0 0;
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.4;
}

.package-hero-type {
  font-weight: 500;
  color: #6b7280;
}

.package-hero-meta-dot {
  color: #d1d5db;
}

.package-hero-subtitle {
  color: #9ca3af;
}

.package-hero-coverage {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  color: #6d28d9;
  background: #f5f3ff;
  border-top: 1px solid #ede9fe;
  transition: background 0.2s ease;
}

.package-hero-coverage:active {
  background: #ede9fe;
}

.checkout-bar-inner {
  position: relative;
  z-index: 1;
  padding: 8px 16px calc(8px + max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px)));
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 -1px 6px rgba(15, 23, 42, 0.04);
}

.checkout-bar-wrap {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(8px);
}

.checkout-bar-wrap::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -80px;
  height: calc(80px + max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px)));
  background: inherit;
  pointer-events: none;
}

.checkout-bar-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 58px;
  padding: 10px 12px 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.checkout-bar-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 52px;
  padding: 14px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: #9ca3af;
  background: #f3f4f6;
  cursor: not-allowed;
}

.checkout-bar-price {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  text-align: left;
}

.checkout-buy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  font-family: inherit;
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.22);
  transition: transform 0.15s ease, opacity 0.2s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.checkout-buy-btn:active:not(:disabled) {
  transform: scale(0.97);
  box-shadow: 0 1px 4px rgba(124, 58, 237, 0.18);
}

.checkout-buy-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkout-price-label {
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  line-height: 1;
}

.checkout-price-row {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 4px;
  line-height: 1.1;
}

.checkout-price-amount {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
}

.checkout-price-currency {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
}

.checkout-action-text {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.checkout-action-chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.9;
}

/* Plan card animations */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* eSIM classic compact plan cards */
.esim-plan-card {
  position: relative;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid #e8eaf0;
  background: #fff;
  box-shadow: 0 1px 6px rgba(15, 23, 42, 0.05);
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  animation: slideInUp 0.35s ease-out forwards;
  box-sizing: border-box;
}

.esim-plan-card:active {
  transform: scale(0.995);
}

.esim-plan-card--active {
  border: 1.5px solid #8b4dff;
  background: #fbf8ff;
  box-shadow: 0 2px 8px rgba(139, 77, 255, 0.06);
}

.esim-plan-badge-float {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 18px;
  padding: 0 7px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #fb923c 0%, #f97316 100%);
  box-shadow: 0 1px 4px rgba(249, 115, 22, 0.28);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
}

.esim-plan-badge-icon {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
}

.esim-plan-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.esim-plan-info {
  flex: 1;
  min-width: 0;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.esim-plan-gb-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.esim-plan-gb-num {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.esim-plan-gb-unit {
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
  line-height: 1.1;
}

.esim-plan-card--active .esim-plan-price {
  color: #7c3aed;
}

.esim-plan-validity-row {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.3;
}

.esim-plan-validity-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  color: #9ca3af;
}

.esim-plan-price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.esim-plan-price {
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  line-height: 1;
  white-space: nowrap;
}

.esim-plan-unit {
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  line-height: 1.3;
  white-space: nowrap;
}

.esim-plan-detail-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  font-size: 11px;
  font-weight: 600;
  color: #8b5cf6;
  white-space: nowrap;
  min-height: 0;
  min-width: 0;
  transition: opacity 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.esim-plan-detail-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.esim-plan-detail-link:active {
  opacity: 0.7;
}

.esim-plan-radio {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 2px solid #d1d5db;
  background: #fff;
  flex-shrink: 0;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.esim-plan-radio--active {
  border-color: #8b5cf6;
  background: #8b5cf6;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.28), 0 0 0 2px rgba(139, 92, 246, 0.14);
  transform: scale(1.04);
}

.esim-plan-radio-core {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}

.esim-plan-radio-check {
  width: 13px;
  height: 13px;
  color: #8b5cf6;
}

.esim-plan-card:nth-child(1) { animation-delay: 0.04s; }
.esim-plan-card:nth-child(2) { animation-delay: 0.08s; }
.esim-plan-card:nth-child(3) { animation-delay: 0.12s; }
.esim-plan-card:nth-child(4) { animation-delay: 0.16s; }
.esim-plan-card:nth-child(5) { animation-delay: 0.2s; }

/* Coverage area expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 20rem;
}

/* Coverage bottom sheet */
.coverage-overlay {
  backdrop-filter: blur(2px);
}

.coverage-sheet-wrap {
  bottom: 0;
  pointer-events: none;
}

.coverage-sheet {
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  pointer-events: auto;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 0 20px calc(20px + max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px), var(--tg-safe-bottom, 0px)));
  box-sizing: border-box;
  box-shadow: 0 -8px 32px rgba(15, 23, 42, 0.12);
  max-height: min(85vh, 560px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.coverage-sheet-handle {
  width: 40px;
  height: 4px;
  margin: 10px auto 14px;
  border-radius: 999px;
  background: #d1d5db;
  flex-shrink: 0;
}

.coverage-sheet-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-shrink: 0;
  padding-bottom: 16px;
}

.coverage-sheet-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.coverage-header-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(139, 92, 246, 0.1);
  color: #7c3aed;
}

.coverage-header-text {
  min-width: 0;
  flex: 1;
}

.coverage-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
}

.coverage-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.35;
}

.coverage-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding-top: 4px;
  box-sizing: border-box;
}

.coverage-region-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  height: 56px;
  padding: 0 14px;
  border: 1px solid #e8eaf0;
  border-radius: 14px;
  background: #fff;
  box-sizing: border-box;
}

.coverage-region-flag-box {
  flex-shrink: 0;
  width: 36px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  background: #f3f4f6;
}

.coverage-region-flag-box--card {
  width: 32px;
  height: 24px;
}

.coverage-region-flag-box--compact {
  width: 28px;
  height: 20px;
  border-radius: 4px;
}

.coverage-region-name {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.coverage-search-box {
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 0 0 12px;
  padding-bottom: 2px;
  background: #fff;
}

.coverage-search-icon {
  position: absolute;
  width: 16px;
  height: 16px;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
}

.coverage-search-input {
  width: 100%;
  height: 36px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 14px;
  padding: 0 12px 0 36px;
  color: #111827;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.coverage-search-input:focus {
  border-color: #c4b5fd;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
}

.coverage-compact-list {
  flex: 1;
  min-height: 0;
  max-height: min(62vh, 480px);
  overflow-x: hidden;
  overflow-y: auto;
  margin: 0 -4px;
  padding: 2px 4px 0;
  box-sizing: border-box;
}

.coverage-compact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  height: 46px;
  padding: 0 4px;
  border-bottom: 1px solid #f0f1f5;
  box-sizing: border-box;
}

.coverage-compact-item:last-child {
  border-bottom: none;
}

.coverage-compact-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.coverage-grid-wrap {
  flex: 1;
  min-height: 0;
  max-height: min(50vh, 360px);
  overflow-x: hidden;
  overflow-y: auto;
  padding-top: 4px;
  box-sizing: border-box;
}

.coverage-grid--cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
}

.coverage-region-card {
  display: flex;
  align-items: center;
  gap: 10px;
  width: calc(50% - 6px);
  height: 72px;
  padding: 0 12px;
  border-radius: 14px;
  border: 1px solid #e8eaf0;
  background: #fff;
  box-sizing: border-box;
  min-width: 0;
}

.coverage-region-card-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* Override page-level .flag-icon min-size inside coverage sheet */
.coverage-sheet .coverage-region-flag.flag-icon {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  min-width: 0 !important;
  min-height: 0 !important;
  max-width: 100% !important;
  max-height: 100% !important;
  margin: 0 !important;
  line-height: 0 !important;
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

.coverage-sheet .coverage-region-flag.flag-icon::before {
  display: none !important;
  content: none !important;
  padding: 0 !important;
}

.coverage-empty {
  padding: 24px 12px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}

.coverage-scroll {
  scrollbar-width: thin;
  scrollbar-color: rgba(139, 92, 246, 0.35) transparent;
}
.coverage-scroll::-webkit-scrollbar {
  width: 4px;
}
.coverage-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.coverage-scroll::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.35);
  border-radius: 2px;
}

/* Pay sheet (checkout) */
.pay-sheet-panel {
  display: flex;
  flex-direction: column;
  max-height: min(
    var(--pay-sheet-panel-max-height, calc(var(--tg-viewport-height, 100dvh) - var(--tg-content-safe-top, 0px) - var(--tg-content-safe-bottom, 0px) - 16px)),
    520px
  );
}

.pay-sheet-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-top: 16px;
  padding-bottom: 4px;
}

.pay-sheet-body::-webkit-scrollbar {
  display: none;
}

.pay-sheet-compact-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f1f4;
}

.pay-sheet-footer {
  flex-shrink: 0;
  padding-top: 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  border-top: 1px solid #f0f1f4;
  background: #fff;
}

.pay-sheet-product {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fafafb;
  border: 1px solid #f0f1f5;
}

.pay-sheet-divider {
  height: 1px;
  margin: 12px 0;
  background: #f0f1f5;
}

.pay-sheet-payment-methods {
  margin-top: 4px;
  padding-bottom: 4px;
}

.pay-sheet-payment-methods-title {
  margin-bottom: 8px;
}

.pay-sheet-payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
}

.pay-sheet-payment-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.pay-sheet-payment-option--active {
  border-color: #8b5cf6;
  background: rgba(139, 92, 246, 0.06);
  box-shadow: 0 0 0 1px rgba(139, 92, 246, 0.12);
}

.pay-sheet-payment-option-title {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.pay-sheet-payment-option-desc {
  font-size: 11px;
  line-height: 1.35;
  color: #6b7280;
}

.pay-sheet-flag-wrap {
  position: relative;
  flex-shrink: 0;
  width: 42px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e8eaef;
  overflow: hidden;
  background: #f9fafb;
}

.pay-sheet-flag.flag-icon {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  min-width: 0 !important;
  min-height: 0 !important;
  background-size: cover !important;
}

.pay-sheet-flag.flag-icon::before {
  display: none !important;
  content: none !important;
}

.pay-sheet-flag-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8b5cf6;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
}

.pay-sheet-product-country {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
}

.pay-sheet-product-detail {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.35;
}

.pay-sheet-promo {
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: gap 0.2s ease;
}

.pay-sheet-collapse-enter-active,
.pay-sheet-collapse-leave-active {
  transition: opacity 0.22s ease, max-height 0.26s ease, margin 0.22s ease;
  overflow: hidden;
}

.pay-sheet-collapse-enter-from,
.pay-sheet-collapse-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.pay-sheet-collapse-enter-to,
.pay-sheet-collapse-leave-from {
  opacity: 1;
  max-height: 56px;
}

.pay-sheet-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 36px;
  padding: 2px 0;
}

.pay-sheet-row--tight {
  min-height: 36px;
  padding: 0;
}

.pay-sheet-row--btn {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.pay-sheet-row--btn:active {
  opacity: 0.85;
}

.pay-sheet-label {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
}

.pay-sheet-row-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.pay-sheet-discount-block {
  padding: 0;
}

.pay-sheet-discount-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 22px;
}

.pay-sheet-discount-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  margin-top: 6px;
}

.pay-sheet-input {
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  line-height: 1.25;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #111827;
  background: #fff;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
}

.pay-sheet-input::placeholder {
  color: #9ca3af;
  text-transform: none;
}

.pay-sheet-input:disabled {
  background: #eceef1;
  color: #b8bcc4;
  border-color: #e2e5ea;
  cursor: not-allowed;
}

.pay-sheet-input:disabled::placeholder {
  color: #c5c9d1;
}

.pay-sheet-input:focus {
  outline: none;
  border-color: #c4b5fd;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.12);
}

.pay-sheet-input:focus:disabled {
  border-color: #e5e7eb;
  box-shadow: none;
}

.pay-sheet-discount-row--disabled .pay-sheet-input {
  background: #eceef1;
  color: #b8bcc4;
  border-color: #e2e5ea;
}

.pay-sheet-discount-row--disabled .pay-sheet-apply-btn:disabled {
  background: #eceef1;
  color: #b0b5bf;
  border-color: #e2e5ea;
}

.pay-sheet-apply-btn {
  flex-shrink: 0;
  min-width: 64px;
  height: 40px;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.pay-sheet-apply-btn:not(:disabled) {
  color: #fff;
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%);
  box-shadow: 0 1px 4px rgba(124, 58, 237, 0.22);
}

.pay-sheet-apply-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.pay-sheet-apply-btn:disabled {
  color: #b0b5bf;
  background: #eceef1;
  border-color: #e2e5ea;
  cursor: not-allowed;
  box-shadow: none;
}

.pay-sheet-coupon-value {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  justify-content: flex-end;
  text-align: right;
}

.pay-sheet-coupon-placeholder {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
}

.pay-sheet-coupon-selected {
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
}

.pay-sheet-coupon-chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #9ca3af;
}

.pay-sheet-hint {
  margin: 6px 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: #c4c0bb;
}

.pay-sheet-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.pay-sheet-total-label {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.pay-sheet-total-prices {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 12px;
}

.pay-sheet-original-price {
  font-size: 11px;
  font-weight: 400;
  color: #b8bcc4;
  text-decoration: line-through;
}

.pay-sheet-total-price {
  font-size: 24px;
  font-weight: 700;
  color: #7c3aed;
  letter-spacing: -0.02em;
}

.pay-sheet-discount-value {
  font-size: 13px;
  font-weight: 700;
  color: #16a34a;
}

.pay-sheet-agree {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  cursor: pointer;
}

.pay-sheet-checkbox {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin: 0;
  border-radius: 4px;
  accent-color: #8b5cf6;
}

.pay-sheet-agree-text {
  font-size: 13px;
  line-height: 1.45;
  color: #4b5563;
}

.pay-sheet-policy-link {
  color: #8b5cf6;
  font-weight: 600;
  text-decoration: none;
}

.pay-sheet-policy-link:active {
  opacity: 0.75;
}

.pay-sheet-confirm-btn {
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  transition: transform 0.15s ease, background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.pay-sheet-confirm-btn:not(:disabled) {
  color: #fff;
  background: #8b5cf6;
  box-shadow: 0 6px 14px rgba(124, 58, 237, 0.18);
}

.pay-sheet-confirm-btn:active:not(:disabled) {
  transform: scale(0.98);
  background: #7c3aed;
}

.pay-sheet-confirm-btn:disabled {
  color: #c4b5fd;
  background: #ede9fe;
  cursor: not-allowed;
  box-shadow: none;
}

/* Telegram Mini App optimizations */
button, [role="button"] {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;
}

/* Prevent text selection on interactive elements */
.cursor-pointer {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Optimize touch targets */
@media (max-width: 768px) {
  button, [role="button"], .cursor-pointer {
    min-height: 44px;
    min-width: 44px;
  }

  .esim-plan-detail-link {
    min-height: 0;
    min-width: 0;
  }

  .pay-sheet-apply-btn {
    min-height: 40px;
    min-width: 0;
  }

  .pay-sheet-row--btn {
    min-height: 0;
    min-width: 0;
  }

  .pay-sheet-confirm-btn {
    min-height: 46px;
    min-width: 0;
  }
}
</style>

<style>
html.package-page-active,
body.package-page-active {
  overflow: hidden;
  height: var(--tg-viewport-height, 100%);
  overscroll-behavior: none;
}

html.dark .package-summary-card,
html.dark .sim-section-card,
html.dark .package-hero-card,
html.dark .esim-plan-card,
html.dark .checkout-bar-panel,
html.dark .coverage-sheet,
html.dark .coverage-region-item,
html.dark .coverage-region-card {
  background: #1f2937;
  border-color: #374151;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}

html.dark .coverage-region-card-name {
  color: #e5e7eb;
}

html.dark .esim-plan-card--active {
  background: rgba(139, 92, 246, 0.12);
  border-color: #8b5cf6;
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.15);
}

html.dark .package-summary-label,
html.dark .package-hero-meta,
html.dark .package-hero-subtitle,
html.dark .checkout-price-label,
html.dark .esim-plan-unit,
html.dark .esim-plan-validity-icon,
html.dark .coverage-subtitle {
  color: #d1d5db;
}

html.dark .package-summary-plan,
html.dark .sim-section-title,
html.dark .package-hero-title,
html.dark .esim-plan-gb-num,
html.dark .esim-plan-price,
html.dark .checkout-price-amount,
html.dark .coverage-title,
html.dark .coverage-region-name,
html.dark .coverage-compact-name,
html.dark .pay-sheet-product-country,
html.dark .pay-sheet-total-label {
  color: #f3f4f6;
}

html.dark .package-summary-plan-strong {
  color: #fff;
}

html.dark .package-summary-empty,
html.dark .package-summary-sms,
html.dark .sim-days-info {
  color: #9ca3af;
}

html.dark .package-hero-type,
html.dark .esim-plan-gb-unit,
html.dark .esim-plan-validity-row,
html.dark .checkout-price-currency,
html.dark .pay-sheet-product-detail,
html.dark .pay-sheet-label,
html.dark .pay-sheet-coupon-placeholder,
html.dark .pay-sheet-coupon-selected {
  color: #d1d5db;
}

html.dark .package-hero-meta-dot {
  color: #4b5563;
}

html.dark .package-hero-coverage {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.12);
  border-top-color: #374151;
}

html.dark .package-hero-coverage:active {
  background: rgba(139, 92, 246, 0.2);
}

html.dark .checkout-bar-inner {
  background: rgba(17, 24, 39, 0.96);
  border-top-color: #374151;
  box-shadow: 0 -1px 6px rgba(0, 0, 0, 0.25);
}

html.dark .checkout-bar-empty {
  background: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

html.dark .esim-plan-radio {
  background: #111827;
  border-color: #4b5563;
}

html.dark .esim-plan-radio--active {
  background: #8b5cf6;
  border-color: #8b5cf6;
}

html.dark .esim-plan-radio-core {
  background: #f9fafb;
}

html.dark .coverage-sheet-handle {
  background: #4b5563;
}

html.dark .coverage-header-icon {
  background: rgba(139, 92, 246, 0.18);
  color: #c4b5fd;
}

html.dark .coverage-compact-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

html.dark .coverage-search-box {
  background: #1f2937;
}

html.dark .coverage-search-icon {
  color: #9ca3af;
}

html.dark .coverage-search-input {
  background: #111827;
  border-color: #4b5563;
  color: #f3f4f6;
}

html.dark .coverage-search-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
}

html.dark .coverage-search-input::placeholder {
  color: #9ca3af;
}

html.dark .coverage-region-flag-box {
  background: #374151;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .coverage-empty {
  color: #9ca3af;
}

html.dark .coverage-scroll {
  scrollbar-color: rgba(139, 92, 246, 0.45) transparent;
}

html.dark .coverage-scroll::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.45);
}

html.dark .sim-option-btn--default {
  background: #374151;
  border-color: #4b5563;
  color: #e5e7eb;
}

html.dark .sim-option-btn--default:not(:disabled):active {
  background: #4b5563;
}

html.dark .sim-option-btn--active {
  background: rgba(139, 92, 246, 0.15);
  border-color: #8b5cf6;
  color: #c4b5fd;
}

html.dark .sim-option-btn--disabled {
  background: #1f2937;
  border-color: #374151;
  color: #6b7280;
}

html.dark .package-hero-flag {
  background: linear-gradient(145deg, #374151 0%, #1f2937 100%);
  border-color: #4b5563;
}

html.dark .pay-sheet-footer {
  background: #111827;
  border-top-color: #374151;
}

html.dark .pay-sheet-compact-total {
  border-top-color: #374151;
}

html.dark .pay-sheet-product {
  background: #111827;
  border-color: #374151;
}

html.dark .pay-sheet-divider {
  background: #374151;
}

html.dark .pay-sheet-payment-option {
  background: #111827;
  border-color: #374151;
}

html.dark .pay-sheet-payment-option--active {
  background: rgba(139, 92, 246, 0.12);
  border-color: #a78bfa;
}

html.dark .pay-sheet-payment-option-title {
  color: #f3f4f6;
}

html.dark .pay-sheet-payment-option-desc {
  color: #9ca3af;
}

html.dark .pay-sheet-flag-wrap {
  background: #374151;
  border-color: #4b5563;
}

html.dark .pay-sheet-input {
  background: #111827;
  border-color: #4b5563;
  color: #f3f4f6;
}

html.dark .pay-sheet-input::placeholder {
  color: #9ca3af;
}

html.dark .pay-sheet-input:disabled,
html.dark .pay-sheet-discount-row--disabled .pay-sheet-input {
  background: #374151;
  color: #9ca3af;
  border-color: #4b5563;
}

html.dark .pay-sheet-apply-btn:disabled,
html.dark .pay-sheet-discount-row--disabled .pay-sheet-apply-btn:disabled {
  background: #374151;
  color: #6b7280;
  border-color: #4b5563;
}

html.dark .pay-sheet-original-price,
html.dark .pay-sheet-hint {
  color: #6b7280;
}

html.dark .pay-sheet-agree-text {
  color: #e5e7eb;
}

html.dark .pay-sheet-policy-link {
  color: #c4b5fd;
}

html.dark .pay-sheet-total-price {
  color: #a78bfa;
}

html.dark .pay-sheet-confirm-btn:not(:disabled) {
  background: #8b5cf6;
  color: #fff;
  box-shadow: 0 6px 14px rgba(124, 58, 237, 0.28);
}

html.dark .pay-sheet-confirm-btn:disabled {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.18);
  border: 1px solid rgba(139, 92, 246, 0.28);
}
</style>
